import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Menu,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  VisuallyHidden,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconDots,
  IconExternalLink,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useEntitlements, usePermission } from "@how/app-kit";
import {
  ConfirmActionModal,
  ErrorState,
  LoadingState,
  PageHeader,
  RecordDrawer,
  StatusBadge,
  useReturnFocus,
  useSearchParamState,
} from "@how/ui";
import { BLOCK_CATALOG, type BlockType } from "@how/website-core";
import { BlockFields, type BlockFieldsContext } from "./BlockFields";
import { useCampuses } from "../campuses/campus-queries";
import { useFindYoutubeChannel } from "./livestream-queries";
import { useSermonChoices } from "./sermon-choices";
import { BlockList, type BlockItem } from "./BlockList";
import { announce, blockName, moveItem } from "./block-order";
import {
  formToSchedule,
  hasSchedule,
  scheduleProblem,
  scheduleToForm,
  usableTimeZone,
  type BlockScheduleValue,
  type ScheduleForm,
} from "./block-schedule";
import { ScheduleFields } from "./ScheduleFields";
import { useTenantRecord } from "../tenant/queries";
import { useForms } from "./form-queries";
import { BLOCK_FIELDS, blockSummary, newBlock, validateBlock } from "./block-fields";
import type { FormRow } from "./form-queries";
import {
  PAGE_STATUS_LABEL,
  pagePath,
  pageStatus,
  useAssets,
  useCreatePreview,
  useDeletePage,
  usePage,
  usePublicHost,
  usePublishPage,
  useRestoreVersion,
  useSaveBlocks,
  useUnpublishPage,
  useUpdatePage,
  type AssetRow,
  type EditorBlock,
  type PageDetail,
  type PageFields,
} from "./website-queries";

const LABEL = new Map(BLOCK_CATALOG.map((entry) => [entry.type, entry.label]));

/** One page: its blocks, its details, and its published history. */
export function PageEditorPage() {
  const { pageId = null } = useParams();
  const page = usePage(pageId);
  if (page.isPending) return <LoadingState />;
  if (page.isError) {
    return (
      <ErrorState
        message="Could not load this page. It may have been deleted."
        onRetry={() => {
          void page.refetch();
        }}
      />
    );
  }
  // Remounting when the draft changes on the server starts the editor from what was just saved, with no effect to
  // copy the fetched page into local state.
  return (
    <PageEditor
      key={`${page.data.page.id}:${page.data.page.draft_updated_at}`}
      detail={page.data}
    />
  );
}

type Block = Record<string, unknown>;

let localKeys = 0;
/** A key for a block not yet saved, which has no id: unique for the editor's life, never sent to the database. */
function localKey(): string {
  localKeys += 1;
  return `new-${String(localKeys)}`;
}

/** The fetched blocks as the editor's list: each keyed by its id, which survives every reorder. */
function toItems(blocks: readonly EditorBlock[]): BlockItem[] {
  return blocks.map((block) => ({
    key: typeof block.id === "string" ? block.id : localKey(),
    block,
  }));
}

function PageEditor({ detail }: { readonly detail: PageDetail }) {
  const navigate = useNavigate();
  const { page } = detail;
  const canManage = usePermission("website.manage", { write: true });
  const canPublish = usePermission("website.publish", { write: true });
  const manage = canManage.data === true;
  const assets = useAssets();
  const forms = useForms();
  const host = usePublicHost();

  const [items, setItems] = useState<BlockItem[]>(() => toItems(detail.blocks));
  const blocks = items.map((item) => item.block);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  // The drawer unmounts when it closes, so it cannot hand focus back itself: remember the Edit button (or the Add
  // menu) that opened it and return there, so a keyboard user keeps their place in the list (UI_STANDARDS, #375).
  const drawerFocus = useReturnFocus();
  const openDrawer = (index: number) => {
    drawerFocus.remember();
    setEditing(index);
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  // In the address, so a save (which reloads the editor) returns to the same tab.
  const [tab, setTab] = useSearchParamState("tab", "content");

  const saveBlocks = useSaveBlocks();
  const publish = usePublishPage();
  const unpublish = useUnpublishPage();
  const deletePage = useDeletePage();
  const preview = useCreatePreview();

  // The church's clock, for show-from / show-until (ADR 0031, amendment of 2026-10-03), and the moment the chips read.
  const tenantRecord = useTenantRecord();
  const timeZone = usableTimeZone(tenantRecord.data?.time_zone);
  // What the Live now block's form needs from the church: its active campuses and zone, and the YouTube channel finder.
  const campuses = useCampuses();
  const findYoutubeChannel = useFindYoutubeChannel();
  // The sermon feature block's series and sermons (ADR 0061), when the church has sermons and media.
  const sermonChoices = useSermonChoices();
  const fieldsContext: BlockFieldsContext = {
    campuses: (campuses.data ?? [])
      .filter((c) => c.isActive)
      .map((c) => ({ id: c.id, name: c.name, timeZone: c.timeZone })),
    churchTimeZone: timeZone,
    findYoutubeChannel,
    sermonChoices,
  };
  const [now] = useState(() => new Date());
  const [moveMessage, setMoveMessage] = useState("");
  const [pendingFocus, setPendingFocus] = useState<{
    readonly key: string;
    readonly control: "handle" | "up" | "down";
  } | null>(null);

  const status = pageStatus(page);
  const invalid = blocks.map((block) => !validateBlock(block).ok);
  const change = (next: BlockItem[]) => {
    setItems(next);
    setDirty(true);
  };

  /*
   * Focus follows the block that moved (UI standards, issue #285): to the same arrow in its new place, or to its
   * handle when that arrow is now disabled (it reached the top or the bottom) or after Move to top.
   */
  useEffect(() => {
    if (pendingFocus === null) return;
    const card = document.querySelector(`[data-block-key="${pendingFocus.key}"]`);
    const wanted =
      card?.querySelector<HTMLElement>(`[data-control="${pendingFocus.control}"]:not(:disabled)`) ??
      card?.querySelector<HTMLElement>('[data-control="handle"]');
    wanted?.focus();
    setPendingFocus(null);
  }, [pendingFocus, items]);

  const move = (from: number, to: number, focus: "handle" | "up" | "down" | null) => {
    const item = items[from];
    if (item === undefined || from === to) return;
    change(moveItem(items, from, to));
    const type = item.block["type"] as BlockType;
    const name = blockName(LABEL.get(type) ?? type, blockSummary(item.block));
    // A drag is announced by the drag itself; the buttons announce here.
    if (focus !== null) {
      setMoveMessage(announce.moved(name, to, items.length));
      setPendingFocus({ key: item.key, control: focus });
    }
  };

  const remove = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    change(next);
    // The control that removed the block went with it: focus moves to the block that took its place.
    const after = next[index] ?? next[index - 1];
    if (after !== undefined) setPendingFocus({ key: after.key, control: "handle" });
  };

  const openPreview = () => {
    // Opened now, while the click still counts as the user's, and pointed at the preview once the token arrives.
    const tab = window.open("", "_blank");
    if (tab !== null) tab.opener = null;
    preview.mutate(page.id, {
      onSuccess: (token) => {
        const url = `https://${host.data ?? ""}${pagePath(page.slug)}?preview=${token}`;
        if (tab === null) window.location.assign(url);
        else tab.location.href = url;
      },
      onError: () => {
        tab?.close();
      },
    });
  };

  return (
    <>
      <PageHeader
        title={page.title}
        description={`Address: ${pagePath(page.published_slug ?? page.slug)}`}
        above={
          <Group gap="xs">
            <Anchor component={Link} to="/website" size="sm">
              Website
            </Anchor>
            <StatusBadge domain="page_status" value={status} label={PAGE_STATUS_LABEL[status]} />
          </Group>
        }
        actions={
          <Group gap="sm">
            <Button
              variant="default"
              leftSection={<IconExternalLink size={16} />}
              loading={preview.isPending}
              disabled={host.data == null || dirty}
              onClick={openPreview}
            >
              Preview
            </Button>
            {canPublish.data === true ? (
              <Button
                disabled={dirty || blocks.length === 0 || status === "published"}
                loading={publish.isPending}
                onClick={() => {
                  publish.mutate(page.id, {
                    onSuccess: (version) => {
                      notifications.show({
                        color: "teal",
                        title: "Published",
                        message: `${page.title} is live as version ${String(version)}.`,
                      });
                    },
                    onError: (error) => {
                      notifications.show({
                        color: "red",
                        title: "Not published",
                        message: error.message,
                      });
                    },
                  });
                }}
              >
                Publish
              </Button>
            ) : null}
            {manage ? (
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="default" size="lg" aria-label="More page actions">
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {canPublish.data === true && status !== "draft" ? (
                    <Menu.Item
                      onClick={() => {
                        unpublish.mutate(page.id, {
                          onSuccess: () => {
                            notifications.show({
                              color: "teal",
                              title: "Taken off the site",
                              message: `${page.title} is no longer public. Its draft is kept.`,
                            });
                          },
                        });
                      }}
                    >
                      Take off the site
                    </Menu.Item>
                  ) : null}
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                  >
                    Delete page
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : null}
          </Group>
        }
      />

      {dirty ? (
        <Alert color="orange" variant="light" mb="md" icon={<IconAlertTriangle size={16} />}>
          You have unsaved changes to this page&apos;s blocks. Save them before previewing or
          publishing.
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(next) => {
          setTab(next ?? undefined);
        }}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="content">Content</Tabs.Tab>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="content">
          <Stack gap="sm">
            {blocks.length === 0 ? (
              <Text size="sm" c="dimmed">
                This page has no blocks yet. Add one to start; a page needs at least one before it
                can be published.
              </Text>
            ) : null}
            {items.length === 0 ? null : (
              <BlockList
                items={items}
                labelOf={(block) => {
                  const type = block["type"] as BlockType;
                  return LABEL.get(type) ?? type;
                }}
                summaryOf={blockSummary}
                invalid={invalid}
                drawable={(block) => BLOCK_FIELDS[block["type"] as BlockType] !== undefined}
                manage={manage}
                timeZone={timeZone}
                now={now}
                onEdit={openDrawer}
                onMove={move}
                onRemove={remove}
              />
            )}
            <VisuallyHidden role="status" aria-live="polite">
              {moveMessage}
            </VisuallyHidden>

            {manage ? (
              <Group justify="space-between" mt="xs">
                <AddBlockMenu
                  disabled={blocks.length >= 40}
                  onAdd={(type) => {
                    const next = [...items, { key: localKey(), block: newBlock(type) }];
                    change(next);
                    openDrawer(next.length - 1);
                  }}
                />
                <Group gap="sm">
                  <Button
                    variant="subtle"
                    disabled={!dirty}
                    onClick={() => {
                      setItems(toItems(detail.blocks));
                      setDirty(false);
                    }}
                  >
                    Discard changes
                  </Button>
                  <Button
                    disabled={!dirty || invalid.some(Boolean)}
                    loading={saveBlocks.isPending}
                    onClick={() => {
                      const cleaned = blocks.map((block) => {
                        const result = validateBlock(block);
                        const id = block["id"];
                        const value = result.ok ? result.value : block;
                        // The contract strips what is not content; the schedule rides beside it (null: always).
                        const schedule = block["schedule"] as BlockScheduleValue | null | undefined;
                        return {
                          ...value,
                          ...(typeof id === "string" ? { id } : {}),
                          schedule: hasSchedule(schedule) ? schedule : null,
                        } as EditorBlock;
                      });
                      saveBlocks.mutate(
                        { pageId: page.id, blocks: cleaned },
                        {
                          onSuccess: () => {
                            notifications.show({
                              color: "teal",
                              title: "Saved",
                              message: "The draft is saved. Publish it when it is ready.",
                            });
                          },
                          onError: (error) => {
                            notifications.show({
                              color: "red",
                              title: "Not saved",
                              message: error.message,
                            });
                          },
                        },
                      );
                    }}
                  >
                    Save changes
                  </Button>
                </Group>
              </Group>
            ) : null}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="details">
          <DetailsForm
            detail={detail}
            assets={assets.data ?? []}
            disabled={!manage}
            blocked={dirty}
          />
        </Tabs.Panel>

        <Tabs.Panel value="history">
          <History detail={detail} canRestore={manage} blocked={dirty} />
        </Tabs.Panel>
      </Tabs>

      {editing === null || blocks[editing] === undefined ? null : (
        <BlockDrawer
          key={editing}
          block={blocks[editing]}
          assets={assets.data ?? []}
          forms={forms.data ?? []}
          disabled={!manage}
          timeZone={timeZone}
          fieldsContext={fieldsContext}
          onClose={() => {
            setEditing(null);
            drawerFocus.restore();
          }}
          onDone={(next) => {
            change(items.map((item, i) => (i === editing ? { ...item, block: next } : item)));
            setEditing(null);
            drawerFocus.restore();
          }}
        />
      )}

      <ConfirmActionModal
        opened={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        title="Delete this page?"
        message={`${page.title} will be taken off the site and removed from this list. Its history is kept, and it can be restored.`}
        confirmLabel="Delete page"
        danger
        loading={deletePage.isPending}
        onConfirm={() => {
          deletePage.mutate(page.id, {
            onSuccess: () => {
              notifications.show({ color: "teal", title: "Page deleted", message: page.title });
              void navigate("/website");
            },
            onError: (error) => {
              setConfirmDelete(false);
              notifications.show({ color: "red", title: "Not deleted", message: error.message });
            },
          });
        }}
      />
    </>
  );
}

function AddBlockMenu({
  disabled,
  onAdd,
}: {
  readonly disabled: boolean;
  readonly onAdd: (type: BlockType) => void;
}) {
  const entitlements = useEntitlements();
  return (
    <Menu position="bottom-start" width={320}>
      <Menu.Target>
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          rightSection={<IconChevronDown size={14} />}
          disabled={disabled}
        >
          Add block
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {BLOCK_CATALOG.map((entry) => {
          // A block that shows a module's data is offered when the church has the module (the sermon feature, ADR 0061).
          const entitled = entry.module === null || entitlements.data?.has(entry.module) === true;
          const available = BLOCK_FIELDS[entry.type] !== undefined && entitled;
          return (
            <Menu.Item
              key={entry.type}
              disabled={!available}
              onClick={() => {
                onAdd(entry.type);
              }}
            >
              <Text size="sm" fw={600}>
                {entry.label}
              </Text>
              <Text size="xs" c="dimmed">
                {available
                  ? entry.description
                  : BLOCK_FIELDS[entry.type] !== undefined && !entitled
                    ? "Needs Sermons and media, from your plan"
                    : "Not available yet"}
              </Text>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

function BlockDrawer({
  block,
  assets,
  forms,
  disabled,
  timeZone,
  fieldsContext,
  onClose,
  onDone,
}: {
  readonly block: Block;
  readonly assets: readonly AssetRow[];
  readonly forms: readonly FormRow[];
  readonly disabled: boolean;
  readonly timeZone: string;
  readonly fieldsContext: BlockFieldsContext;
  readonly onClose: () => void;
  readonly onDone: (next: Block) => void;
}) {
  const type = block["type"] as BlockType;
  const [value, setValue] = useState<Block>(block);
  const [schedule, setSchedule] = useState<ScheduleForm>(() =>
    scheduleToForm(block["schedule"] as BlockScheduleValue | null | undefined, timeZone),
  );
  const [problems, setProblems] = useState<string[]>([]);
  return (
    <RecordDrawer
      opened
      onClose={onClose}
      title={LABEL.get(type) ?? type}
      subtitle={
        <Text size="sm" c="dimmed" mb="md">
          {BLOCK_CATALOG.find((e) => e.type === type)?.description}
        </Text>
      }
    >
      <Stack gap="md">
        <BlockFields
          fields={BLOCK_FIELDS[type] ?? []}
          value={value}
          assets={assets}
          forms={forms}
          disabled={disabled}
          context={fieldsContext}
          onChange={(next) => {
            setValue(next);
          }}
        />
        <ScheduleFields
          value={schedule}
          timeZone={timeZone}
          disabled={disabled}
          onChange={setSchedule}
        />
        {problems.length > 0 ? (
          <Alert
            color="red"
            variant="light"
            title="Fix these before continuing"
            icon={<IconAlertTriangle size={16} />}
          >
            <Stack gap={2}>
              {problems.map((problem) => (
                <Text key={problem} size="sm">
                  {problem}
                </Text>
              ))}
            </Stack>
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {disabled ? "Close" : "Cancel"}
          </Button>
          {disabled ? null : (
            <Button
              onClick={() => {
                const result = validateBlock(value);
                const windowProblem =
                  schedule.mode === "dates"
                    ? scheduleProblem(schedule.from, schedule.until, timeZone)
                    : null;
                if (result.ok && windowProblem === null) {
                  onDone({ ...value, schedule: formToSchedule(schedule, timeZone) });
                } else {
                  setProblems([
                    ...(result.ok ? [] : result.problems),
                    ...(windowProblem === null ? [] : [windowProblem]),
                  ]);
                }
              }}
            >
              Done
            </Button>
          )}
        </Group>
      </Stack>
    </RecordDrawer>
  );
}

function DetailsForm({
  detail,
  assets,
  disabled,
  blocked,
}: {
  readonly detail: PageDetail;
  readonly assets: readonly AssetRow[];
  readonly disabled: boolean;
  /** Unsaved block changes: saving details reloads the page, which would discard them. */
  readonly blocked: boolean;
}) {
  const { page } = detail;
  const update = useUpdatePage();
  const [fields, setFields] = useState<PageFields>({
    title: page.title,
    slug: page.slug,
    description: page.description ?? "",
    seoTitle: page.seo_title ?? "",
    seoDescription: page.seo_description ?? "",
    socialImageId: page.social_image_id,
    noindex: page.noindex,
  });
  const set =
    (key: "title" | "slug" | "description" | "seoTitle" | "seoDescription") =>
    (event: { currentTarget: { value: string } }) => {
      const next = event.currentTarget.value;
      setFields((current) => ({ ...current, [key]: next }));
    };
  const taken = (update.error as { code?: string } | null)?.code === "23505";
  const livesElsewhere = page.published_slug !== null && page.published_slug !== fields.slug;

  return (
    <Stack gap="sm" maw={720}>
      <TextInput
        label="Title"
        required
        disabled={disabled}
        value={fields.title}
        onChange={set("title")}
      />
      <TextInput
        label="Address"
        required
        disabled={disabled}
        leftSection={<Text size="sm">/</Text>}
        description={
          livesElsewhere
            ? `Visitors still reach this page at ${pagePath(page.published_slug ?? "")} until you publish again.`
            : "Lowercase words joined by hyphens. home is the home page."
        }
        error={taken ? "Another page already uses this address." : undefined}
        value={fields.slug}
        onChange={set("slug")}
      />
      <Textarea
        label="Summary"
        description="A sentence about the page, used by search engines when there is no search description."
        disabled={disabled}
        autosize
        minRows={2}
        value={fields.description}
        onChange={set("description")}
      />
      <TextInput
        label="Search title"
        description="Shown in search results. Leave empty to use the page title."
        disabled={disabled}
        maxLength={70}
        value={fields.seoTitle}
        onChange={set("seoTitle")}
      />
      <Textarea
        label="Search description"
        disabled={disabled}
        autosize
        minRows={2}
        maxLength={300}
        value={fields.seoDescription}
        onChange={set("seoDescription")}
      />
      <Select
        label="Image when shared"
        description="Shown when someone shares a link to this page."
        placeholder={assets.length === 0 ? "No images uploaded yet" : "None"}
        clearable
        disabled={disabled || assets.length === 0}
        data={assets.map((a) => ({ value: a.id, label: a.alt_text }))}
        value={fields.socialImageId}
        onChange={(next) => {
          setFields((current) => ({ ...current, socialImageId: next }));
        }}
      />
      <Checkbox
        label="Hide this page from search engines"
        disabled={disabled}
        checked={fields.noindex}
        onChange={(event) => {
          const next = event.currentTarget.checked;
          setFields((current) => ({ ...current, noindex: next }));
        }}
      />
      {disabled ? null : (
        <Group justify="flex-end">
          <Button
            disabled={
              blocked || fields.title.trim().length === 0 || fields.slug.trim().length === 0
            }
            loading={update.isPending}
            onClick={() => {
              update.mutate(
                { pageId: page.id, fields },
                {
                  onSuccess: () => {
                    notifications.show({
                      color: "teal",
                      title: "Details saved",
                      message: "Publish to make them public.",
                    });
                  },
                },
              );
            }}
          >
            Save details
          </Button>
        </Group>
      )}
      {update.isError && !taken ? (
        <Text size="sm" c="red">
          The details were not saved. Check the address uses only lowercase letters, numbers,
          hyphens and slashes.
        </Text>
      ) : null}
    </Stack>
  );
}

function History({
  detail,
  canRestore,
  blocked,
}: {
  readonly detail: PageDetail;
  readonly canRestore: boolean;
  readonly blocked: boolean;
}) {
  const restore = useRestoreVersion();
  const [confirming, setConfirming] = useState<PageDetail["versions"][number] | null>(null);
  if (detail.versions.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This page has never been published. Each time it is, the published version is kept here.
      </Text>
    );
  }
  return (
    <>
      <Table.ScrollContainer minWidth={420}>
        <Table verticalSpacing="sm" aria-label="Published versions">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Version</Table.Th>
              <Table.Th>Published</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {detail.versions.map((version) => (
              <Table.Tr key={version.id}>
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      {version.version}
                    </Text>
                    {version.id === detail.page.published_version_id ? (
                      <Badge variant="light" color="teal" size="sm">
                        Live
                      </Badge>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(version.published_at).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  {canRestore ? (
                    <Button
                      variant="subtle"
                      size="xs"
                      disabled={blocked}
                      aria-label={`Restore version ${String(version.version)} into the draft`}
                      onClick={() => {
                        setConfirming(version);
                      }}
                    >
                      Restore into draft
                    </Button>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <ConfirmActionModal
        opened={confirming !== null}
        onClose={() => {
          setConfirming(null);
        }}
        title="Restore this version into the draft?"
        message={`The draft of ${detail.page.title} will be replaced by version ${String(confirming?.version ?? "")}. Visitors see no change until you publish.`}
        confirmLabel="Restore"
        loading={restore.isPending}
        onConfirm={() => {
          if (confirming === null) return;
          restore.mutate(
            { pageId: detail.page.id, versionId: confirming.id },
            {
              onSuccess: () => {
                notifications.show({
                  color: "teal",
                  title: "Restored into the draft",
                  message: "Publish to make it live.",
                });
                setConfirming(null);
              },
            },
          );
        }}
      />
    </>
  );
}
